import { countriesList } from '@cmg/common';
import { Alert, Divider, Section, Stack, Typography } from '@cmg/design-system';
import { FormattedDate, FormattedTime } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import { useMemo } from 'react';

import { parseLocalDateTime } from '../../common/util/timing';
import { OfferingStage, OfferingType } from '../../graphql';
import { OfferingDetails_PublishedOfferingFragment } from '../offering-side-panel/offering-details/graphql/__generated__';
import { DocumentationSection } from './components/DocumentationSection';
import { GeographicInvestorRestrictionSection } from './components/GeographicInvestorRestrictionSection';
import { KeyValuePair } from './components/KeyValuePair';
import { ManagersSection } from './components/ManagersSection';
import { ConvertibleTermsSection } from './components/terms-section/ConvertibleTermsSection';
import { TermsSection } from './components/terms-section/TermsSection';

type OfferingProfileProps = {
  offering: OfferingDetails_PublishedOfferingFragment;
};

export function OfferingProfile({ offering }: Readonly<OfferingProfileProps>) {
  const isErroneous = offering.stage === OfferingStage.Erroneous;

  const { bookOpenDate, bookOpenTime, bookCloseDate, bookCloseTime, timeZone } = offering.timing;
  const openDateTime = parseLocalDateTime(bookOpenDate, bookOpenTime, timeZone);
  const closeDateTime = parseLocalDateTime(bookCloseDate, bookCloseTime, timeZone);
  const pricingInstrument = useMemo(
    () => offering?.instruments.find(instrument => instrument.id === offering.pricingInstrumentId),
    [offering]
  );
  const securityTypeDisplayName = pricingInstrument?.isDepositaryReceipt
    ? 'ADS/ADR'
    : offering.security.typeDisplayName;
  const offeringCountryCode = defaultTo(
    offering.instruments[0].countryCode,
    countriesList.Usa.countryCode
  );

  return (
    <Stack spacing={2} data-testid="offering-profile">
      {isErroneous && <Alert severity="info">This offering has been discarded</Alert>}
      <GeographicInvestorRestrictionSection
        registrationLinks={offering.disclaimers?.registrationLinks ?? []}
        restrictions={offering.disclaimers?.restrictions}
      />
      <DocumentationSection prospectusDocuments={offering.prospectusDocuments ?? []} />
      <Section title="Issuer Details">
        <KeyValuePair
          label="Business Description"
          value={offering.issuer.businessDescription ?? '-'}
        />
        <KeyValuePair label="Security Type" value={securityTypeDisplayName ?? '-'} />
      </Section>
      <Section title="Timing">
        <Typography variant="body1">
          All dates are presented in exchange country’s local time zone.
        </Typography>
        <KeyValuePair
          label="Pricing Date"
          value={
            <FormattedDate value={offering.timing.pricingDate} format="literal" timeZone="UTC" />
          }
        />
        <KeyValuePair
          label="Book Open Date"
          value={<FormattedDate value={bookOpenDate} format="literal" timeZone="UTC" />}
        />
        <KeyValuePair
          label="Book Open Time"
          value={
            <FormattedTime
              value={openDateTime}
              timeZone={timeZone ?? undefined}
              timeZoneName="short"
            />
          }
        />
        <KeyValuePair
          label="Book Close Date"
          value={<FormattedDate value={bookCloseDate} format="literal" timeZone="UTC" />}
        />
        <KeyValuePair
          label="Book Close Time"
          value={
            <FormattedTime
              value={closeDateTime}
              timeZone={timeZone ?? undefined}
              timeZoneName="short"
            />
          }
        />
        <KeyValuePair
          label="First Trade Date"
          value={
            <FormattedDate value={offering.timing.firstTradeDate} format="literal" timeZone="UTC" />
          }
        />
        <KeyValuePair
          label="Settlement Date"
          value={
            <FormattedDate value={offering.timing.settlementDate} format="literal" timeZone="UTC" />
          }
        />
      </Section>
      {offering.type === OfferingType.Convertible ? (
        <ConvertibleTermsSection
          terms={offering.terms}
          pricingInstrument={pricingInstrument ?? null}
          pricingCurrencyCode={offering.pricingCurrencyCode}
          offeringStatus={offering.status}
        />
      ) : (
        <TermsSection
          offeringType={offering.type}
          terms={offering.terms}
          pricingCurrencyCode={offering.pricingCurrencyCode}
        />
      )}
      <ManagersSection
        syndicate={defaultTo(offering.syndicate, null)}
        offeringCountryCode={offeringCountryCode}
      />
      <Section title="Marketing">
        <KeyValuePair
          label="Roadshow Details"
          value={
            <Stack spacing={1}>
              {offering.roadshowSchedules?.length
                ? offering.roadshowSchedules.map(({ id, date, details }) => (
                    <Typography variant="body1" key={id}>
                      <FormattedDate value={date} format="literal" timeZone="UTC" /> – {details}
                    </Typography>
                  ))
                : '-'}
            </Stack>
          }
        />
        <KeyValuePair label="Management Team" value={offering.marketing?.managementTeam ?? '-'} />
        <Stack py={1}>
          <Divider />
        </Stack>
        <KeyValuePair
          label="Investor Call Details"
          value={offering.marketing?.callDetails ?? '-'}
        />
      </Section>
      {offering.type === OfferingType.Convertible ? (
        <Section title="Notices & Disclaimers">
          <Stack data-testid="convertible-offering-profile-notices">
            <Typography variant="body2" color="text.secondary">
              {offering.disclaimers?.rule134Legend}
            </Typography>
          </Stack>
        </Section>
      ) : (
        <Section title="Notices & Disclaimers">
          <Typography variant="body2" color="secondary">
            {offering.disclaimers?.footnotes ?? '-'}
          </Typography>
          <Stack py={1}>
            <Divider />
          </Stack>
          <Typography variant="body2" color="secondary">
            {offering.disclaimers?.rule134Legend ?? '-'}
          </Typography>
        </Section>
      )}
    </Stack>
  );
}
